import React, { memo, useEffect, useState } from "react";

import cn from "classnames";
import { defineMessages, useIntl } from "react-intl";

import { LoadingSpinner } from "@mapmycustomers/ui";

import styles from "./TextFilePreview.module.scss";

const MAX_PREVIEWABLE_BYTES = 1_000_000;

const messages = defineMessages({
  tooLarge: {
    id: "component.filePreview.text.tooLarge",
    defaultMessage: "File is too large to preview. Download it to view the contents.",
    description: "Shown when a text-based file exceeds the inline preview size cap",
  },
  unreadable: {
    id: "component.filePreview.text.unreadable",
    defaultMessage: "Couldn't read the file contents.",
    description: "Shown when reading a text-based file's blob into a string fails",
  },
});

interface TextFilePreviewProps {
  className?: string;
  file: Blob;
}

const TextFilePreview = ({ className, file }: TextFilePreviewProps) => {
  const intl = useIntl();
  const [text, setText] = useState<null | string>(null);
  const [error, setError] = useState<"tooLarge" | "unreadable" | null>(null);

  useEffect(() => {
    // Reset before re-reading so swapping `file` doesn't leave the
    // previous file's text or error on screen during the new read.
    setText(null);
    setError(null);
    if (file.size > MAX_PREVIEWABLE_BYTES) {
      setError("tooLarge");
      return;
    }
    let cancelled = false;
    file
      .text()
      .then((contents) => {
        if (!cancelled) {
          // Strip the UTF-8 BOM Excel/Windows tools prepend, else it shows as a stray glyph.
          setText(contents.replace(/^\uFEFF/, ""));
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError("unreadable");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [file]);

  if (error === "tooLarge") {
    return <div className={styles.message}>{intl.formatMessage(messages.tooLarge)}</div>;
  }
  if (error === "unreadable") {
    return <div className={styles.message}>{intl.formatMessage(messages.unreadable)}</div>;
  }
  if (text === null) {
    return <LoadingSpinner />;
  }
  return <pre className={cn(styles.text, className)}>{text}</pre>;
};

export default memo(TextFilePreview);
